import { useState, useEffect } from "react";
import { getEmailSettings, saveEmailSettings, testEmailConnection, pollEmailsNow, testSendEmail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Server, Lock, RefreshCw, CheckCircle, XCircle, Clock, Send } from "lucide-react";
import { usePermissions } from "@/context/PermissionContext";

export default function EmailSettingsPage() {
  const { isExposition } = usePermissions();
  const [settings, setSettings] = useState({ host: "", port: 993, email: "", password: "", poll_interval: 120, smtp_host: "send.one.com", smtp_port: 465 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [polling, setPolling] = useState(false);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getEmailSettings();
      setConfigured(res.data.configured);
      setSettings(prev => ({
        ...prev,
        host: res.data.host || "",
        port: res.data.port || 993,
        email: res.data.email || "",
        poll_interval: res.data.poll_interval || 120,
        smtp_host: res.data.smtp_host || "send.one.com",
        smtp_port: res.data.smtp_port || 465
      }));
    } catch {
      // email settings not configured yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.host || !settings.email || (!settings.password && !configured)) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      setSaving(true);
      await saveEmailSettings(settings);
      toast.success("Email settings saved successfully");
      setConfigured(true);
    } catch (err) {
      toast.error("Failed to save email settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await testEmailConnection();
      if (res.data.success) {
        toast.success("IMAP connection successful!");
      } else {
        toast.error(`Connection failed: ${res.data.message}`);
      }
    } catch (err) {
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleTestSend = async () => {
    try {
      setSendingTest(true);
      const res = await testSendEmail();
      if (res.data.success) {
        toast.success(res.data.message || "Test email sent!");
      } else {
        toast.error(`Send failed: ${res.data.message}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const handlePollNow = async () => {
    try {
      setPolling(true);
      const res = await pollEmailsNow();
      toast.success(`Processed ${res.data.processed} emails: ${res.data.flights} flights, ${res.data.lvo} LVO`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Poll failed - check IMAP settings");
    } finally {
      setPolling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#d4af37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Email Integration Settings</h1>
        <p className="text-gray-400 text-sm mt-1">Configure IMAP to automatically read incoming flight and LVO emails</p>
      </div>

      {/* Status */}
      <div className={`flex items-center gap-3 p-3 rounded-lg border ${configured ? "bg-green-500/10 border-green-500/30" : "bg-yellow-500/10 border-yellow-500/30"}`}>
        {configured ? <CheckCircle size={18} className="text-green-400" /> : <XCircle size={18} className="text-yellow-400" />}
        <span className={`text-sm ${configured ? "text-green-400" : "text-yellow-400"}`}>
          {configured ? "IMAP is configured" : "IMAP not yet configured"}
        </span>
      </div>

      {/* Settings Form */}
      <fieldset disabled={isExposition} className="bg-[#0f172a] border border-gray-700 rounded-lg p-6 space-y-4 min-w-0 disabled:opacity-70">
        <div>
          <label className="text-gray-400 text-sm block mb-1 flex items-center gap-1"><Server size={14} /> IMAP Host *</label>
          <Input 
            value={settings.host}
            onChange={e => setSettings(prev => ({ ...prev, host: e.target.value }))}
            placeholder="imap.gmail.com"
            className="bg-[#020617] border-gray-600 text-white"
            data-testid="imap-host"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-gray-400 text-sm block mb-1">IMAP Port</label>
            <Input 
              type="number"
              value={settings.port}
              onChange={e => setSettings(prev => ({ ...prev, port: parseInt(e.target.value) || 993 }))}
              className="bg-[#020617] border-gray-600 text-white"
              data-testid="imap-port"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm block mb-1 flex items-center gap-1"><Clock size={14} /> Poll Interval (seconds)</label>
            <Input 
              type="number"
              value={settings.poll_interval}
              onChange={e => setSettings(prev => ({ ...prev, poll_interval: parseInt(e.target.value) || 120 }))}
              className="bg-[#020617] border-gray-600 text-white"
              data-testid="poll-interval"
            />
          </div>
        </div>
        <div>
          <label className="text-gray-400 text-sm block mb-1 flex items-center gap-1"><Mail size={14} /> Email Address *</label>
          <Input 
            value={settings.email}
            onChange={e => setSettings(prev => ({ ...prev, email: e.target.value }))}
            placeholder="operations@airport.com"
            className="bg-[#020617] border-gray-600 text-white"
            data-testid="imap-email"
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm block mb-1 flex items-center gap-1"><Lock size={14} /> App Password *</label>
          <Input 
            type="password"
            value={settings.password}
            onChange={e => setSettings(prev => ({ ...prev, password: e.target.value }))}
            placeholder={configured ? "••••••••  (leave empty to keep current)" : "Enter app password"}
            className="bg-[#020617] border-gray-600 text-white"
            data-testid="imap-password"
          />
        </div>

        {/* Outbound SMTP (for password reset / notification emails) */}
        <div className="pt-2 mt-2 border-t border-gray-700">
          <p className="text-gray-300 text-sm font-medium mb-3 flex items-center gap-2"><Send size={14} /> Outbound Email (SMTP)</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 text-sm block mb-1 flex items-center gap-1"><Server size={14} /> SMTP Host</label>
              <Input
                value={settings.smtp_host}
                onChange={e => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                placeholder="send.one.com"
                className="bg-[#020617] border-gray-600 text-white"
                data-testid="smtp-host"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm block mb-1">SMTP Port (SSL)</label>
              <Input
                type="number"
                value={settings.smtp_port}
                onChange={e => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 465 }))}
                placeholder="465"
                className="bg-[#020617] border-gray-600 text-white"
                data-testid="smtp-port"
              />
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-2">Reuses the mailbox above to send password-reset codes. one.com uses send.one.com:465 (SSL).</p>
        </div>

        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <Button onClick={handleSave} disabled={saving} className="bg-[#d4af37] hover:bg-[#b8972e] text-[#0f172a]" data-testid="save-email-settings">
            {saving ? "Saving..." : "Save Settings"}
          </Button>
          {configured && (
            <>
              <Button onClick={handleTest} disabled={testing} variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10" data-testid="test-connection">
                {testing ? "Testing..." : "Test IMAP"}
              </Button>
              <Button onClick={handleTestSend} disabled={sendingTest} variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10" data-testid="test-send">
                <Send size={14} className="mr-1" />
                {sendingTest ? "Sending..." : "Send Test Email"}
              </Button>
              <Button onClick={handlePollNow} disabled={polling} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10" data-testid="poll-now">
                <RefreshCw size={14} className={`mr-1 ${polling ? "animate-spin" : ""}`} />
                {polling ? "Polling..." : "Poll Now"}
              </Button>
            </>
          )}
        </div>
      </fieldset>

      {/* Info */}
      <div className="bg-[#0f172a] border border-gray-700 rounded-lg p-4">
        <h3 className="text-white font-medium text-sm mb-2">How it works</h3>
        <ul className="text-gray-400 text-xs space-y-1 list-disc list-inside">
          <li>The system polls the configured mailbox for new unread emails</li>
          <li>AI (GPT-5.2) analyzes each email to extract flight data or detect LVO notifications</li>
          <li>Flight emails create pending requests that can be reviewed and approved</li>
          <li>LVO emails trigger a notification popup for all users to accept or decline</li>
          <li>You can also submit emails manually from the Pending Flights page</li>
        </ul>
      </div>
    </div>
  );
}
